import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import NavbarComp from './components/navbar';
import ProfileSidebar from './components/profileSidebar';
import Description from './components/description';
import Skills from './components/skills';
import Portfolio from './components/portfolio';
import Contact from './components/contact';
import Experience from './components/experience';
import { hashToTab, profileTabs } from './data/profileTabs';
import { useTranslation } from './i18n/LanguageContext';
import { resolvePageInfo } from './utils/pageInfo';
import {
  Container,
  Footer,
  LoadingScreen,
  MainColumn,
  PageGrid,
  ProfileTabs,
  TabPanel
} from './AppStyles';

const PAGE_INFO_URL =
  'https://s3.us-east-2.amazonaws.com/davidrzuluaga.com/assets/pageinfo.json';

const getInitialColorMode = () => {
  if (typeof window === 'undefined') return 'dark';

  const stored = window.localStorage.getItem('color-mode');
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }

  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }

  return 'dark';
};

const App = () => {
  const { locale, t } = useTranslation();
  const [rawPageInfo, setRawPageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() =>
    hashToTab(window.location.hash)
  );
  const [colorMode, setColorMode] = useState(getInitialColorMode);

  useEffect(() => {
    axios
      .get(PAGE_INFO_URL, {
        headers: {
          'Cache-Control': 'no-cache'
        }
      })
      .then(response => {
        setRawPageInfo(response.data[0]);
      })
      .catch(err => {
        console.log(err);
        setError('loadError');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const pageInfo = useMemo(
    () => resolvePageInfo(rawPageInfo, locale),
    [rawPageInfo, locale]
  );

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', colorMode);
    window.localStorage.setItem('color-mode', colorMode);
  }, [colorMode]);

  useEffect(() => {
    const onHashChange = () => {
      setActiveTab(hashToTab(window.location.hash));
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleTabChange = tabId => {
    setActiveTab(tabId);
    const hash = tabId === 'overview' ? '#overview' : `#${tabId}`;
    if (window.location.hash !== hash) {
      window.history.replaceState(null, '', hash);
    }
  };

  const handleToggleColorMode = () => {
    setColorMode(mode => (mode === 'light' ? 'dark' : 'light'));
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            <Description pageInfo={pageInfo} variant='readme' />
            <Portfolio pageInfo={pageInfo} />
          </>
        );
      case 'skills':
        return <Skills pageInfo={pageInfo} />;
      case 'portfolio':
        return <Portfolio pageInfo={pageInfo} />;
      case 'experience':
        return <Experience pageInfo={pageInfo} />;
      case 'contact':
        return <Contact pageInfo={pageInfo} variant='main' />;
      default:
        return null;
    }
  };

  if (loading) {
    return <LoadingScreen>{t('common.loading')}</LoadingScreen>;
  }

  if (error || !pageInfo) {
    return <LoadingScreen>{t('common.loadError')}</LoadingScreen>;
  }

  return (
    <div>
      <NavbarComp
        pageInfo={pageInfo}
        onTabChange={handleTabChange}
        colorMode={colorMode}
        onToggleColorMode={handleToggleColorMode}
      />

      <Container>
        <PageGrid>
          <ProfileSidebar pageInfo={pageInfo} />

          <MainColumn>
            <ProfileTabs role='tablist' aria-label={t('tabs.profileSections')}>
              {profileTabs.map(tab => (
                <button
                  key={tab.id}
                  type='button'
                  role='tab'
                  id={`tab-${tab.id}`}
                  aria-selected={activeTab === tab.id}
                  aria-controls={`panel-${tab.id}`}
                  className={activeTab === tab.id ? 'active' : undefined}
                  onClick={() => handleTabChange(tab.id)}
                >
                  <i className={tab.icon} />
                  {t(`tabs.${tab.id}`)}
                </button>
              ))}
            </ProfileTabs>

            <TabPanel
              role='tabpanel'
              id={`panel-${activeTab}`}
              aria-labelledby={`tab-${activeTab}`}
            >
              {renderTabContent()}
            </TabPanel>
          </MainColumn>
        </PageGrid>
      </Container>

      <Footer>{t('common.footer')}</Footer>
    </div>
  );
};

export default App;
